import { Component } from "react"
import gql from "graphql-tag"
import { graphql } from "@apollo/client/react/hoc"
import { Alert, Button, Card, CardBody, UncontrolledTooltip } from "reactstrap"
import { Link } from "react-router-dom"

import "./IncidentDetailHeader.css"
import { HasPermissionsQuery } from "../Functions/querys"
import DatetimeRenderer from "../Material/DatetimeRenderer"
import ProgressTrackingBar from "../Material/ProgressTrackingBar"
import ConfirmationPopup from "../Material/ConfirmationPopup"
import withApolloClient from "../withApolloClient"
import ArchiveReasonModal from "./IncidentModals/ArchiveReasonModal"
import toTitleCase from "../Functions/toTitleCase"
import { normalizeTaskTabHash } from "../TaskManagement/taskNavigation"

const IncidentStatusChoicesQuery = gql`
    query IncidentStatusChoices {
        statusChoices: __type(name: "IncidentsIncidentStatusChoices") {
            enumValues {
                name
                description
            }
        }
    }
`

const DeleteIncidentMutation = gql`
    mutation DeleteIncidentMutation($input: DeleteIncidentMutationInput!) {
        deleteIncident(input: $input) {
            ok
            errors {
                messages
            }
        }
    }
`

const UpdateIncidentMutation = gql`
    mutation UpdateIncidentMutation($input: UpdateIncidentMutationInput!) {
        updateIncident(input: $input) {
            ok
            errors {
                messages
            }
        }
    }
`

export class IncidentDetailHeader extends Component {
    constructor(props) {
        super(props)
        this.state = {
            loading: false,
            error: "",
            archiveError: "",
            detachInspectionError: "",
            openModal: "",
            showArchiveConfirm: false,
            archiveReason: "",
            showFullArchiveReason: false,
            copiedField: "",
        }
    }

    componentWillUnmount() {
        clearTimeout(this.copyFeedbackTimeout)
    }

    copyValue = async (value, fieldName) => {
        if (value === undefined || value === null || value === "") {
            return
        }

        try {
            await navigator.clipboard.writeText(String(value))
            clearTimeout(this.copyFeedbackTimeout)
            this.setState({ copiedField: fieldName })
            this.copyFeedbackTimeout = setTimeout(() => {
                this.setState((previousState) => ({
                    copiedField: previousState.copiedField === fieldName ? "" : previousState.copiedField,
                }))
            }, 2000)
        } catch (error) {
            this.setState({ error: `Could not copy ${fieldName}: ${error.toString()}` })
        }
    }

    renderCopyableValue = (value, fieldName, prefix = "") => {
        if (value === undefined || value === null || value === "") {
            return "--"
        }

        const isCopied = this.state.copiedField === fieldName
        return (
            <button
                type="button"
                className={`incident-detail-header__copyable${isCopied ? " is-copied" : ""}`}
                onClick={() => this.copyValue(value, fieldName)}
                title={isCopied ? `${toTitleCase(fieldName)} copied` : `Copy ${toTitleCase(fieldName)}`}
            >
                <span>{prefix}{value}</span>
                <i className={`fa ${isCopied ? "fa-check" : "fa-clipboard"}`} aria-hidden="true" />
            </button>
        )
    }

    toggleModal = (modalName) => {
        const isArchiveModal = modalName === "archiveIncident"
        const isOpen = this.state.openModal === modalName
        const isArchived = this.props.incident?.isArchived
        this.setState({
            openModal: isOpen ? "" : modalName,
            ...(isArchiveModal && {
                showArchiveConfirm: isArchived,
                archiveReason: "",
                archiveError: "",
            }),
        })
    }

    handleArchiveReasonContinue = (reason) => {
        this.setState({ showArchiveConfirm: true, archiveReason: reason || "", archiveError: "" })
    }

    toggleArchiveReasonView = () => {
        this.setState((previousState) => ({
            showFullArchiveReason: !previousState.showFullArchiveReason,
        }))
    }

    deleteIncident = (incidentId) => {
        try {
            if (!incidentId) {
                this.setState({ error: "Missing Required Field" })
                return
            }

            this.setState({ loading: true })
            const input = { incidentId }
            this.props.apolloClient.mutate({
                mutation: DeleteIncidentMutation,
                variables: { input },
            }).then((result) => {
                if (result?.data?.deleteIncident?.ok) {
                    this.setState({ error: "", successMessage: "Successfully Deleted Incident" })
                    setTimeout(() => {
                        window.location.href = "/incidents"
                    }, 1500)
                } else {
                    const errorMessage = result?.data?.deleteIncident?.errors?.[0]?.messages?.toString()
                        || "An error occurred, could not complete request."
                    this.setState({ error: errorMessage })
                }
                this.setState({ loading: false })
            }).catch(() => {
                this.setState({ error: "An error has occurred", loading: false })
            })
        } catch (error) {
            this.setState({ error: `An error has occurred: ${error.toString()}`, loading: false })
        }
    }

    toggleArchiveIncident = (incidentId, isArchived) => {
        try {
            if (!incidentId) {
                this.setState({ archiveError: "Missing Required Field" })
                return
            }

            this.setState({ loading: true, archiveError: "" })
            const input = {
                incidentId,
                isArchived: !isArchived,
            }
            if (!isArchived && this.state.archiveReason) {
                input.archiveReason = this.state.archiveReason.trim()
            }
            this.props.apolloClient.mutate({
                mutation: UpdateIncidentMutation,
                variables: { input },
            }).then((result) => {
                if (result?.data?.updateIncident?.ok) {
                    this.setState({
                        error: "",
                        archiveError: "",
                        successMessage: isArchived
                            ? "Successfully Un-Archived Incident"
                            : "Successfully Archived Incident",
                        loading: false,
                        openModal: "",
                        archiveReason: "",
                        showArchiveConfirm: false,
                    })
                    setTimeout(() => {
                        window.location.reload()
                    }, 1500)
                } else {
                    const errorMessage = result?.data?.updateIncident?.errors?.[0]?.messages?.toString()
                        || "An error occurred, could not complete request."
                    this.setState({ archiveError: errorMessage, loading: false })
                }
            }).catch((error) => {
                this.setState({ archiveError: `An error has occurred: ${error.toString()}`, loading: false })
            })
        } catch (error) {
            this.setState({ archiveError: `An error has occurred: ${error.toString()}`, loading: false })
        }
    }

    detachInspection = (incidentId) => {
        try {
            if (!incidentId) {
                this.setState({ detachInspectionError: "Missing Required Field" })
                return
            }

            this.setState({ loading: true, detachInspectionError: "" })
            const input = { incidentId, detachInspection: true }
            this.props.apolloClient.mutate({
                mutation: UpdateIncidentMutation,
                variables: { input },
            }).then((result) => {
                if (result?.data?.updateIncident?.ok) {
                    this.setState({
                        detachInspectionError: "",
                        successMessage: "Successfully Detached Inspection",
                        loading: false,
                        openModal: "",
                    })
                    setTimeout(() => {
                        window.location.reload()
                    }, 1500)
                } else {
                    const errorMessage = result?.data?.updateIncident?.errors?.[0]?.messages?.toString()
                        || "An error occurred, could not complete request."
                    this.setState({ detachInspectionError: errorMessage, loading: false })
                }
            }).catch((error) => {
                this.setState({ detachInspectionError: `An error has occurred: ${error.toString()}`, loading: false })
            })
        } catch (error) {
            this.setState({ detachInspectionError: `An error has occurred: ${error.toString()}`, loading: false })
        }
    }

    fetchMetaData = (metadata = null, name) => {
        if (!metadata || metadata === "") {
            return null
        }
        try {
            metadata = JSON.parse(metadata.replace(/'/g, '"'))
        } catch {
            metadata = null
        }
        return metadata?.[name] || null
    }

    getDefaultPartyDriver = () => {
        const { incident } = this.props
        const defaultParty = incident?.incidentpartySet?.edges?.find(({ node }) => node?.isDefault)?.node
        const defaultPartyDriver = defaultParty?.incidentpartypeopleSet?.edges?.find(({ node }) =>
            ["VEHICLE_DRIVER", "UNAUTHORIZED_DRIVER", "EMPLOYEE", "VENDOR"].includes(node?.role)
        )?.node

        if (defaultPartyDriver) {
            return {
                label: toTitleCase(defaultPartyDriver.role),
                name: defaultPartyDriver.name,
                role: defaultPartyDriver.role,
            }
        }

        return {
            label: "DRIVER",
            name: incident?.driver?.name,
            role: "VEHICLE_DRIVER",
        }
    }

    render() {
        const { hasPermissions, incident, statusChoices } = this.props
        const activeTab = normalizeTaskTabHash(window.location.hash)
        const liabilityClaims = incident?.liabilityclaimSet?.edges?.map(({ node }) => node) || []
        const latestLiabilityClaim = liabilityClaims
            .filter((claim) => claim?.dateAdded)
            .sort((firstClaim, secondClaim) => new Date(secondClaim.dateAdded) - new Date(firstClaim.dateAdded))[0]
            || liabilityClaims[0]
            || null
        const defaultPartyDriver = this.getDefaultPartyDriver()
        const shouldLinkDriver = defaultPartyDriver.role === "VEHICLE_DRIVER" && incident?.driver?.id
        const statusDescription = statusChoices?.enumValues?.find(({ name }) => name === incident?.status)?.description

        return (
            <>
                {this.state.error && <Alert color="danger">{this.state.error}</Alert>}
                <Card className="incident-detail-header">
                    <CardBody className="incident-detail-header__body">
                        <div className="incident-detail-header__breadcrumb">
                            <Link to="/incidents">Incidents</Link>
                            <span>/</span>
                            <span className="incident-detail-header__breadcrumb-value">
                                {this.renderCopyableValue(incident?.pk, "incident id", "#")}
                            </span>
                            <span>/</span>
                            <span className="incident-detail-header__breadcrumb-value incident-detail-header__case-title">
                                {this.renderCopyableValue(incident?.title, "incident title")}
                            </span>
                        </div>

                        <div className="incident-detail-header__main">
                            <div className="incident-detail-header__identity">
                                <div
                                    className="incident-detail-header__title-line"
                                    role="group"
                                    aria-label="Incident status and data progress"
                                >
                                    <h2 className="incident-detail-header__vehicle-title">
                                        <Link
                                            to={`/car/${incident?.car?.pk}`}
                                            className="incident-detail-header__link"
                                        >
                                            {incident?.car?.year}{" "}
                                            {toTitleCase(incident?.car?.carModel?.make)}{" "}
                                            {incident?.car?.carModel?.name}
                                            <i
                                                className="fa fa-arrow-up incident-detail-header__external-link-icon"
                                                role="img"
                                                aria-label="Opens in new tab"
                                            />
                                        </Link>
                                    </h2>

                                    {incident?.status && !incident?.isRecordOnly && (
                                        <>
                                            <span
                                                id="incidentStatus"
                                                className={`incident-detail-header__badge incident-detail-header__badge--${incident.status === "COMPLETED"
                                                        ? "success"
                                                        : incident.status === "WORKING_ON_IT"
                                                            ? "warning"
                                                            : incident.status === "PROCESSED"
                                                                ? "dark"
                                                                : "secondary"
                                                    }`}
                                            >
                                                {statusDescription || toTitleCase(incident.status) || "--"}
                                            </span>
                                            <UncontrolledTooltip target="incidentStatus">Incident Status</UncontrolledTooltip>
                                        </>
                                    )}

                                    {incident?.carOnHold && (
                                        <span className="incident-detail-header__badge incident-detail-header__badge--danger">
                                            CAR ON HOLD
                                        </span>
                                    )}
                                    {incident?.driver?.hasPendingInsurance && (
                                        <span className="incident-detail-header__badge incident-detail-header__badge--danger">
                                            PENDING INSURANCE
                                        </span>
                                    )}
                                    {incident?.driver?.hasIncompleteReturn && (
                                        <span className="incident-detail-header__badge incident-detail-header__badge--info">
                                            HAS INCOMPLETE RETURN
                                        </span>
                                    )}
                                    {incident?.isRecordOnly && (
                                        <>
                                            <span
                                                className="incident-detail-header__badge incident-detail-header__badge--record"
                                                id="recordOnlyTooltip"
                                            >
                                                Record Only
                                            </span>
                                            <UncontrolledTooltip target="recordOnlyTooltip">
                                                Record-Only Incident
                                            </UncontrolledTooltip>
                                        </>
                                    )}
                                    {incident?.subroClaimExists && (
                                        <span className="incident-detail-header__badge incident-detail-header__badge--subro">
                                            SUBROGATION
                                        </span>
                                    )}

                                    <div className="incident-detail-header__progress">
                                        <span className="incident-detail-header__label">DATA PROGRESS</span>
                                        <ProgressTrackingBar
                                            progress={incident?.dataProgress}
                                            missingDataFields={incident?.missingDataFields}
                                        />
                                    </div>
                                </div>
                                <p className="incident-detail-header__vin">
                                    VIN: {incident?.car?.vin || "--"}
                                </p>
                            </div>

                            <div className="incident-detail-header__actions">
                                {hasPermissions?.includes("archive_incident") && !incident?.isArchived && (
                                    <Button
                                        className="incident-detail-header__action incident-detail-header__action--archive"
                                        onClick={() => this.toggleModal("archiveIncident")}
                                    >
                                        Archive
                                    </Button>
                                )}
                                {!incident?.damageTransaction?.id
                                    && !incident?.incidentdamageSet?.edges?.length
                                    && hasPermissions?.includes("delete_incident") && (
                                        <Button
                                            className="incident-detail-header__action incident-detail-header__action--delete"
                                            onClick={() => this.toggleModal("deleteIncident")}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                {incident?.carCondition && activeTab === "#inspection" && hasPermissions?.includes("custom_can_detach_inspection") && (
                                    <Button
                                        className="incident-detail-header__action incident-detail-header__action--delete"
                                        onClick={() => this.toggleModal("detachInspection")}
                                    >
                                        Detach Inspection
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="incident-detail-header__divider" />

                        <div className="incident-detail-header__facts">
                            <div className="incident-detail-header__fact">
                                <span className="incident-detail-header__label">CREATED</span>
                                <span className="incident-detail-header__value">
                                    {incident?.dateAdded
                                        ? <DatetimeRenderer datetime={incident.dateAdded} />
                                        : "--"}
                                </span>
                            </div>
                            <div className="incident-detail-header__fact">
                                <span className="incident-detail-header__label">{defaultPartyDriver.label}</span>
                                <span className="incident-detail-header__value">
                                    {defaultPartyDriver.name ? (
                                        shouldLinkDriver ? (
                                            <Link
                                                to={`/driver/${incident?.driver?.id}`}
                                                className="incident-detail-header__link incident-detail-header__link--underlined"
                                            >
                                                {defaultPartyDriver.name}
                                                <i
                                                    className="fa fa-arrow-up incident-detail-header__external-link-icon"
                                                    role="img"
                                                    aria-label="Opens in new tab"
                                                />
                                            </Link>
                                        ) : defaultPartyDriver.name
                                    ) : "No driver attached"}
                                </span>
                            </div>
                            <div className="incident-detail-header__fact">
                                <span className="incident-detail-header__label">INCIDENT DATE</span>
                                <span className="incident-detail-header__value">
                                    {incident?.incidentDate
                                        ? <DatetimeRenderer datetime={incident.incidentDate} />
                                        : "No incident date added"}
                                </span>
                            </div>
                            <div className="incident-detail-header__fact incident-detail-header__fact--location">
                                <span className="incident-detail-header__label">LOCATION</span>
                                <span className="incident-detail-header__value">
                                    {incident?.location ? (
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(incident.location)}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="incident-detail-header__location-link"
                                        >
                                            {incident.location}
                                        </a>
                                    ) : "No location provided"}
                                </span>
                            </div>
                            <div className="incident-detail-header__fact">
                                <span className="incident-detail-header__label">BRANCH</span>
                                <span className="incident-detail-header__value">
                                    {incident?.branch?.name || "No Branch"}
                                </span>
                            </div>
                            <div className="incident-detail-header__fact">
                                <span className="incident-detail-header__label">LIABILITY CLAIM #</span>
                                <span className="incident-detail-header__value">
                                    {latestLiabilityClaim?.externalId ? (
                                        this.renderCopyableValue(
                                            latestLiabilityClaim.externalId,
                                            "liability claim external id"
                                        )
                                    ) : "--"}
                                </span>
                            </div>
                        </div>
                    </CardBody>

                    {this.state.openModal === "deleteIncident" && (
                        <ConfirmationPopup
                            open
                            title="Delete Incident"
                            loading={this.state.loading}
                            message="Are you sure you want to delete this incident?"
                            handleClose={() => this.toggleModal("deleteIncident")}
                            action="Delete Incident"
                            confirmAction={() => this.deleteIncident(incident?.id)}
                            color="danger"
                            error={this.state.error}
                            success={this.state.successMessage || ""}
                        />
                    )}

                    {this.state.openModal === "archiveIncident"
                        && !this.state.showArchiveConfirm
                        && !incident?.isArchived && (
                            <ArchiveReasonModal
                                isOpen
                                isArchived={incident?.isArchived}
                                onCancel={() => this.toggleModal("archiveIncident")}
                                onContinue={this.handleArchiveReasonContinue}
                            />
                        )}

                    {this.state.openModal === "detachInspection" && (
                        <ConfirmationPopup
                            open
                            title="Detach Inspection"
                            loading={this.state.loading}
                            message="Are you sure you want to detach the inspection from this incident?"
                            handleClose={() => this.toggleModal("detachInspection")}
                            action="Detach Inspection"
                            confirmAction={() => this.detachInspection(incident?.id)}
                            color="danger"
                            error={this.state.detachInspectionError}
                            success={this.state.successMessage || ""}
                        />
                    )}

                    {this.state.openModal === "archiveIncident" && this.state.showArchiveConfirm && (
                        <ConfirmationPopup
                            open
                            title={incident?.isArchived ? "Un-Archive Incident" : "Archive Incident"}
                            loading={this.state.loading}
                            message={`Are you sure you want to ${incident?.isArchived ? "un-archive" : "archive"} this incident?`}
                            handleClose={() => this.toggleModal("archiveIncident")}
                            action={incident?.isArchived ? "Un-Archive Incident" : "Archive Incident"}
                            confirmAction={() => this.toggleArchiveIncident(incident?.id, incident?.isArchived)}
                            color={incident?.isArchived ? "success" : "warning"}
                            error={this.state.archiveError}
                            success={this.state.successMessage || ""}
                        />
                    )}
                </Card>

                {incident?.isArchived && (
                    <div className="incident-detail-header__archived-alert">
                        <div>
                            <p className="incident-detail-header__archived-title mb-0">
                                <b>
                                    <i className="fa fa-archive" aria-hidden="true" /> This incident is{" "}
                                    <span style={{ color: "#d9534f" }}>archived</span> and is read-only.
                                </b>
                            </p>
                            <div className="incident-detail-header__archived-reason pt-1">
                                <p className="mb-0">
                                    <b>Archive Reason: </b>
                                    {(() => {
                                        const reason = this.fetchMetaData(incident?.metadata, "archive_reason")
                                            || "No reason provided."
                                        const words = reason.trim().split(/\s+/)
                                        const isLong = words.length > 15
                                        return (
                                            <>
                                                {isLong && !this.state.showFullArchiveReason
                                                    ? `${words.slice(0, 15).join(" ")}...`
                                                    : reason}
                                                {isLong && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0 ml-2"
                                                        onClick={this.toggleArchiveReasonView}
                                                    >
                                                        {this.state.showFullArchiveReason ? "View less" : "View more"}
                                                    </button>
                                                )}
                                            </>
                                        )
                                    })()}
                                </p>
                            </div>
                        </div>
                        {hasPermissions?.includes("archive_incident") && (
                            <button
                                className="incident-detail-header__unarchive"
                                onClick={() => this.toggleModal("archiveIncident")}
                            >
                                <i className="fa fa-undo" />&nbsp;Un-Archive
                            </button>
                        )}
                    </div>
                )}
            </>
        )
    }
}

export default graphql(IncidentStatusChoicesQuery, {
    props: ({ data: { statusChoices } }) => ({ statusChoices }),
})(
    graphql(HasPermissionsQuery, {
        options: () => ({
            variables: { userPermissions: ["archive_incident", "delete_incident", "custom_can_detach_inspection"] },
        }),
        props: ({ data: { hasPermissions } }) => ({ hasPermissions }),
    })(withApolloClient(IncidentDetailHeader))
)
